import Box from '@mui/material/Box';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { useContext, useEffect, useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import Select from 'react-select';
import BadgeStatus from '../../../../components/Common/BadgeStatus';
import ToastUtil from '../../../../components/ToastNotification/ToastUtil';
import { GET, newFetch, POST } from '../../../../helpers/fetch';
import { getSelectedSiteIds } from '../../../../helpers/general';
import AppContext from '../../../../store/App/contexts';

const ClaimsApproval = ({ history }) => {
  const { selectedClientID, selectedSiteID } = useContext(AppContext);
  const clientID = parseInt(selectedClientID, 10);
  const siteID = parseInt(selectedSiteID, 10);
  const siteIDs = getSelectedSiteIds(clientID, siteID);
  const siteIDsKey = siteIDs.join(',');
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [claimTypes, setClaimTypes] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [sortModel, setSortModel] = useState([
    { field: 'updatedAt', sort: 'desc' },
  ]);
  const [totalRows, setTotalRows] = useState(0);
  const [data, setData] = useState([]);
  const type = localStorage.getItem('type');
  const [clients, setClients] = useState([]);
  const [searchText, setSearchText] = useState('');

  // State for multi-select filters
  const [nameFilter, setNameFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);

  // Filter options extracted from claims data
  const [nameOptions, setNameOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  // NEW: Selection and modal state for bulk actions
  const [selectionModel, setSelectionModel] = useState([]);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [remarksText, setRemarksText] = useState('');

  const columns = [
    {
      field: 'id',
      headerName: 'Request ID',
      flex: 0.6,
      sortable: true,
      resizable: false,
      filterable: false,
      renderCell: (params) => (
        <Link
          className="text-primary"
          to={`/claim/approval/edit/${params.row.id}/approver/${params.row.approver_level}`}
          style={{ fontWeight: 700, fontSize: 14 }}
        >
          {params.row.id}
        </Link>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (params) => <BadgeStatus status={params.row.status} />,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 0.7,
      sortable: true,
      resizable: false,
      filterable: false,
      renderCell: (params) => (
        <span className="text-black-default">{params.row.name || '--'}</span>
      ),
    },
    {
      field: 'client',
      headerName: 'Client',
      flex: 0.7,
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (params) => (
        <span className="text-muted" style={{ fontWeight: 400 }}>
          {params.row.client || '--'}
        </span>
      ),
    },
    {
      field: 'site',
      headerName: 'Site',
      flex: 0.7,
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (params) => (
        <span className="text-muted" style={{ fontWeight: 400 }}>
          {params.row.site || '--'}
        </span>
      ),
    },
    {
      field: 'date_of_receipt',
      headerName: 'Date of Receipt',
      flex: 0.5,
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (params) => (
        <span
          className="text-muted"
          style={{ color: '#212529', fontSize: 14, fontWeight: 400 }}
        >
          {params.row.date_of_receipt || '--'}
        </span>
      ),
    },
    {
      field: 'claim_type_name',
      headerName: 'Type',
      flex: 0.5,
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (params) => (
        <span className="text-black-default">
          {params.row.claim_type_name || '--'}
        </span>
      ),
    },
    {
      field: 'receipt_no',
      headerName: 'Receipt No.',
      flex: 0.5,
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (params) => (
        <span
          className="text-muted"
          style={{ color: '#212529', fontSize: 14, fontWeight: 400 }}
        >
          {params.row.receipt_no || '--'}
        </span>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 0.5,
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (params) => (
        <span
          className="text-muted"
          style={{ color: '#212529', fontSize: 14, fontWeight: 400 }}
        >
          {params.row.amount || '--'}
        </span>
      ),
    },
    {
      field: 'deduct_amount',
      headerName: 'Reimbursement',
      flex: 0.5,
      sortable: false,
      resizable: false,
      filterable: false,
      renderCell: (params) => (
        <span
          className="text-muted"
          style={{ color: '#212529', fontSize: 14, fontWeight: 400 }}
        >
          {params.row.deduct_amount || '--'}
        </span>
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Date Modified',
      flex: 1,
      sortable: true,
      resizable: false,
      filterable: false,
      renderCell: (params) => (
        <span
          className="text-muted"
          style={{ color: '#212529', fontSize: 14, fontWeight: 400 }}
        >
          {params.row.updatedAt || '--'}
        </span>
      ),
    },
  ];

  const handleSelectionChange = (newSelection) => {
    setSelectionModel(newSelection);
  };

  const handleClaimApproval = async (action, remarks = null) => {
    if (!selectionModel || selectionModel.length === 0) {
      return ToastUtil.error('Please select at least one claim request');
    }

    try {
      const response = await newFetch(POST, '/claims/bulk-approval', {
        action,
        claim_ids: selectionModel,
        remarks,
      });

      if (response.success) {
        const pastTense = action === 'approve' ? 'approved' : 'rejected';
        const claimText = selectionModel.length === 1 ? 'claim' : 'claims';

        ToastUtil.success(
          `Successfully ${pastTense} ${selectionModel.length} ${claimText}`
        );

        // Refresh the claims list
        const { field, sort } = sortModel[0] || {};
        fetchClaims(paginationModel, field, sort, searchText);
        setSelectionModel([]);
        setRemarksText('');
        setOpenRejectModal(false);
      } else {
        ToastUtil.error(response.message || `${action} failed`);
      }
    } catch (err) {
      ToastUtil.error('Network error during approval');
      console.error(`${action} exception:`, err);
    }
  };

  // Extract unique filter options from claims data
  const extractFilterOptions = (claimsData) => {
    if (!claimsData || claimsData.length === 0) return;

    const nameMap = new Map();
    claimsData
      .filter(
        (claim) => claim.name && claim.name.trim() !== '' && claim.associate_id
      )
      .forEach((claim) => {
        if (!nameMap.has(claim.associate_id)) {
          nameMap.set(claim.associate_id, {
            id: claim.associate_id,
            name: claim.name.trim(),
          });
        }
      });

    const nameOpts = Array.from(nameMap.values()).map((item) => ({
      value: item.id,
      label: item.name,
    }));

    const uniqueStatuses = [
      ...new Set(
        claimsData
          .filter((claim) => claim.status && claim.status.trim() !== '')
          .map((claim) => claim.status)
      ),
    ];

    const statusOpts = uniqueStatuses.map((status) => ({
      value: status,
      label: status,
    }));

    setNameOptions(nameOpts);
    setStatusOptions(statusOpts);
  };

  const fetchClaims = async (pagination, field, sort, search) => {
    try {
      setLoading(true);
      const fetchUrl = '/claims/approval/list';
      const response = await newFetch(POST, fetchUrl, {
        field,
        sort,
        page: pagination.page,
        pageSize: pagination.pageSize,
        client_id: clientID,
        site_id: siteID,
        site_ids: siteIDs,
        type,
        search: search || '',
        names: nameFilter.map((opt) => opt.value),
        types: typeFilter.map((opt) => opt.value),
        statuses: statusFilter.map((opt) => opt.value),
      });

      if (!response.success) {
        console.log('error');
      } else {
        setClaims(response.data.list);
        setTotalRows(response.data.total_record);

        if (nameOptions.length === 0 || statusOptions.length === 0) {
          extractFilterOptions(response.data.list);
        }

        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const fetchAllClaimsForFilters = async () => {
    try {
      const fetchUrl = '/claims/approval/list';
      const response = await newFetch(POST, fetchUrl, {
        field: 'updatedAt',
        sort: 'desc',
        page: 0,
        pageSize: 1000,
        client_id: clientID,
        site_id: siteID,
        site_ids: siteIDs,
        type,
        search: '',
        names: [],
        types: [],
        statuses: [],
      });

      if (response.success && response.data.list) {
        extractFilterOptions(response.data.list);
      }
    } catch (err) {
      console.log('Error fetching claims for filters:', err);
    }
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  const fetchTypes = async () => {
    try {
      let api_path = `/claimtypes/${siteID}?client_id=${clientID}&site_ids=${siteIDs.join(
        ','
      )}`;
      let response = await newFetch(GET, api_path);

      if (response.success) {
        let claimTypes = response.data;
        setClaimTypes(claimTypes);
      } else {
        ToastUtil.success(response.message);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchClients = async () => {
    let api_path = `/client/${clientID}`;
    let response = await newFetch(GET, api_path);

    if (response.success) {
      setClients(response.data);
    }
  };

  const searchHandler = (e) => {
    setSearchText(e.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  useEffect(() => {
    if (clientID) fetchTypes();
  }, [clientID, siteIDsKey]);

  useEffect(() => {
    setNameFilter([]);
    setTypeFilter([]);
    setStatusFilter([]);
    setNameOptions([]);
    setStatusOptions([]);

    if (clientID) {
      fetchAllClaimsForFilters();
    }
  }, [clientID, siteIDsKey]);

  useEffect(() => {
    const { field, sort } = sortModel[0] || {};
    fetchClaims(paginationModel, field, sort, searchText);
    fetchClients();
  }, [
    sortModel,
    paginationModel,
    clientID,
    siteID,
    siteIDsKey,
    nameFilter,
    typeFilter,
    statusFilter,
  ]);

  useEffect(() => {
    const { field, sort } = sortModel[0] || {};
    const delayDebounce = setTimeout(() => {
      fetchClaims(paginationModel, field, sort, searchText);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchText, paginationModel, sortModel]);

  useEffect(() => {
    setData(claims !== undefined ? claims : []);
    claims !== undefined && setLoading(false);
  }, [claims]);

  const typeOptions = Array.from(
    new Map(
      [
        ...claimTypes.map((type) => ({
          value: type.id,
          label: type.name,
        })),
        ...claims
          .filter((claim) => claim.type_id && claim.claim_type_name)
          .map((claim) => ({
            value: claim.type_id,
            label: claim.claim_type_name,
          })),
      ].map((option) => [Number(option.value), option])
    ).values()
  );

  return (
    <>
      <div
        className="container-fluid"
        style={{
          paddingLeft: '2rem',
          paddingRight: '2rem',
          paddingBottom: '6rem',
        }}
      >
        <div className="border-bottom pb-2 py-2 mb-2 d-flex align-items-center">
          <h6 className="flex-grow-1 pt-4" style={{ color: '#A6478E' }}>
            Claims | For Approval
          </h6>
        </div>
        {clients.enable_claims ? (
          <>
            {/* Multi-select filters */}
            <div className="row mb-3">
              <div className="col-lg-3 col-md-6">
                <div className="mb-3">
                  <label
                    htmlFor="type-filter"
                    className="form-label font-size-13 text-muted"
                  >
                    Type
                  </label>
                  <Select
                    value={typeFilter}
                    onChange={setTypeFilter}
                    isMulti
                    options={typeOptions}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder="Select Types..."
                  />
                </div>
              </div>
            </div>

            {/* NEW: Bulk Action Buttons */}
            <div className="d-flex gap-3 mb-2">
              <button
                onClick={() => handleClaimApproval('approve')}
                className="btn btn-brand adjust-fs adjust-shadow"
                style={{ width: '12rem' }}
                disabled={!selectionModel || selectionModel.length === 0}
              >
                Approve
              </button>
              <button
                onClick={() => setOpenRejectModal(true)}
                className="btn btn-danger adjust-fs adjust-shadow"
                style={{ width: '12rem' }}
                disabled={!selectionModel || selectionModel.length === 0}
              >
                Reject
              </button>
            </div>

            <div className="table-responsive rounded shadow bg-white">
              <Box sx={{ width: '100%', height: 1000 }}>
                {data && (
                  <DataGrid
                    columns={columns}
                    rows={data || []}
                    getRowId={(row) => row.id}
                    rowCount={totalRows || 1}
                    loading={loading}
                    pageSizeOptions={[10, 20, 30, 40, 50, 80, 100]}
                    paginationModel={paginationModel}
                    paginationMode="server"
                    onPaginationModelChange={setPaginationModel}
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 10 },
                      },
                    }}
                    style={{ minHeight: 100, fontSize: 14 }}
                    localeText={{ noRowsLabel: '' }}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    getRowHeight={() => 'auto'}
                    sx={{
                      [`& .${gridClasses.cell}`]: { py: 3 },
                      [`& .${gridClasses.columnHeader}`]: {
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#6c757d!important',
                      },
                      [`& .${gridClasses.row}`]: {
                        fontSize: '14px',
                        color: '#A6478E!important',
                        fontWeight: 700,
                      },
                    }}
                    checkboxSelection
                    onRowSelectionModelChange={handleSelectionChange}
                    rowSelectionModel={selectionModel}
                    disableRowSelectionOnClick={true}
                  />
                )}
              </Box>
            </div>
          </>
        ) : (
          <div className="text-muted pt-4">
            The client does not have access to this module.
          </div>
        )}
      </div>

      {openRejectModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content p-3">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleClaimApproval('reject', remarksText);
                }}
                className="d-flex flex-column"
              >
                <h5>Remarks</h5>
                <textarea
                  className="form-control my-3"
                  rows={4}
                  value={remarksText}
                  onChange={(e) => setRemarksText(e.target.value)}
                  placeholder="Enter remarks..."
                />
                <div className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setOpenRejectModal(false);
                      setRemarksText('');
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-danger">
                    Reject
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default withRouter(ClaimsApproval);
